import { useState, useEffect } from 'react';
import * as apiClient from '../../lib/api';
import { useAuth } from './AuthProvider';
import { Check, ExternalLink, Copy } from 'lucide-react';

const DISCORD_INVITE = 'https://discord.gg/DdDttFJh';

export default function DiscordLink() {
  const { sessionToken } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [verificationCode, setVerificationCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [discordStatus, setDiscordStatus] = useState<apiClient.DiscordStatus | undefined>(undefined);

  useEffect(() => {
    if (!sessionToken) return;
    apiClient.discord.status().then(setDiscordStatus).catch(() => setDiscordStatus({ linked: false }));
  }, [sessionToken]);

  const handleGenerateCode = async () => {
    if (!sessionToken) return;

    setIsGenerating(true);
    setError(null);

    try {
      const result = await apiClient.discord.generateVerificationCode();
      setVerificationCode(result.code);
    } catch (err: any) {
      setError(err.message || 'Failed to generate code');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyCode = () => {
    if (verificationCode) {
      navigator.clipboard.writeText(verificationCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleUnlink = async () => {
    if (!sessionToken) return;

    setIsGenerating(true);
    setError(null);

    try {
      await apiClient.discord.unlink();
      setDiscordStatus({ linked: false });
      setVerificationCode(null);
    } catch (err: any) {
      setError(err.message || 'Failed to unlink Discord');
    } finally {
      setIsGenerating(false);
    }
  };

  // Loading state
  if (discordStatus === undefined) {
    return (
      <div className="discord-link">
        <div className="discord-link-loading">Loading...</div>
      </div>
    );
  }

  // Already linked
  if (discordStatus.linked) {
    return (
      <div className="discord-link discord-linked discord-link-embedded">
        <div className="discord-status">
          <Check size={16} className="discord-check" />
          <span>Discord linked: <strong>{discordStatus.username}</strong></span>
        </div>

        <p className="discord-description">
          If you lose access to your device, use the <strong>/recover</strong> command in our Discord server to get a recovery code.
        </p>

        {error && <div className="discord-error">{error}</div>}

        <button
          type="button"
          onClick={handleUnlink}
          disabled={isGenerating}
          className="discord-unlink"
        >
          {isGenerating ? 'Unlinking...' : 'Unlink Discord'}
        </button>
      </div>
    );
  }

  // Not linked - show link flow
  return (
    <div className="discord-link discord-link-embedded">
      {verificationCode ? (
        // Show code to copy
        <div className="discord-code-display">
          <p className="discord-code-label">Your verification code:</p>
          <div className="discord-code-box">
            <span className="discord-code">{verificationCode}</span>
            <button onClick={handleCopyCode} className="discord-copy-btn">
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div className="discord-steps">
            <div className="discord-step">
              <span className="step-number">1</span>
              <span>
                Join our{' '}
                <a href={DISCORD_INVITE} target="_blank" rel="noopener noreferrer" className="discord-server-link">
                  Discord server
                  <ExternalLink size={12} />
                </a>
              </span>
            </div>
            <div className="discord-step">
              <span className="step-number">2</span>
              <span>Use command: <strong>/verify {verificationCode}</strong></span>
            </div>
          </div>
          <p className="discord-code-expiry">Code expires in 10 minutes</p>
        </div>
      ) : (
        // Show generate button
        <>
          <div className="discord-steps">
            <div className="discord-step">
              <span className="step-number">1</span>
              <span>Generate a verification code below</span>
            </div>
            <div className="discord-step">
              <span className="step-number">2</span>
              <span>
                Join our{' '}
                <a href={DISCORD_INVITE} target="_blank" rel="noopener noreferrer" className="discord-server-link">
                  Discord server
                  <ExternalLink size={12} />
                </a>
              </span>
            </div>
            <div className="discord-step">
              <span className="step-number">3</span>
              <span>Use <strong>/verify [code]</strong> in Discord</span>
            </div>
          </div>

          {error && <div className="discord-error">{error}</div>}

          <button
            type="button"
            onClick={handleGenerateCode}
            disabled={isGenerating}
            className="discord-generate-btn"
          >
            {isGenerating ? 'Generating...' : 'Generate Verification Code'}
          </button>
        </>
      )}
    </div>
  );
}
